import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, useMediaQuery, useTheme } from '@mui/material';

import { TableTitleBox } from '../../common/components';

import { useBankProjectedReport } from '../../hooks/useBankProjectedReport';
import { useBankAccounts } from '../../hooks/useBankAccounts';
import ProjectedReportTable from './components/ProjectedReportTable';
import ProjectedReportFilters from './components/ProjectedReportFilters';
import { MAX_PERIODS } from './constants/projectedReportConstants';
import { formatBankName } from '../bank-movements/utils/formatters';
import { generateProjectedReportPdf } from './utils/pdfGenerationUtils';
import { exportProjectedReportToExcel } from './utils/exportExcel';

const formatDateForInput = (date) => {
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().split('T')[0];
};

const addDays = (dateString, days) => {
  const base = new Date(dateString);
  if (Number.isNaN(base.getTime())) {
    return dateString;
  }
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return formatDateForInput(result);
};

const createPeriodId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `period-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const buildInitialPeriods = (from, to) => [{ id: createPeriodId(), from, to }];

const BankProjectedReport = () => {
  const today = useMemo(() => new Date(), []);
  const todayInput = useMemo(() => formatDateForInput(today), [today]);
  const defaultFrom = todayInput;
  const defaultTo = useMemo(() => addDays(todayInput, 6), [todayInput]);

  const [periods, setPeriods] = useState(() => buildInitialPeriods(defaultFrom, defaultTo));
  const [filters, setFilters] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [exportError, setExportError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [isRefetchingFromBankChange, setIsRefetchingFromBankChange] = useState(false);

  const { data: report, isFetching, isError, error, refetch } = useBankProjectedReport(filters);
  const { data: bankAccounts = [] } = useBankAccounts();

  const projectionBounds = useMemo(() => {
    const completedPeriods = periods.filter((period) => period.from && period.to);

    if (!completedPeriods.length) {
      return { from: '', to: '' };
    }

    const orderedStarts = completedPeriods
      .map((period) => period.from)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    const orderedEnds = completedPeriods
      .map((period) => period.to)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

    return {
      from: orderedStarts[0],
      to: orderedEnds[orderedEnds.length - 1],
    };
  }, [periods]);

  const availableBanks = useMemo(() => {
    const bankSet = new Set();
    bankAccounts.forEach((account) => {
      if (account.bank_name) {
        bankSet.add(account.bank_name);
      }
    });
    return Array.from(bankSet).sort();
  }, [bankAccounts]);

  const reportBanks = useMemo(() => {
    const bankSet = new Set();
    report?.periods?.forEach((period) => {
      period.banks?.forEach((bank) => bankSet.add(bank.bank_name));
    });
    return Array.from(bankSet).sort();
  }, [report]);

  const allBanks = availableBanks.length > 0 ? availableBanks : reportBanks;

  const bankColumns = useMemo(() => {
    if (reportBanks.length === 0) {
      return [];
    }
    return reportBanks.filter((bank) => selectedBanks.includes(bank));
  }, [reportBanks, selectedBanks]);

  useEffect(() => {
    if (availableBanks.length > 0 && selectedBanks.length === 0) {
      setSelectedBanks([...availableBanks]);
    }
  }, [availableBanks, selectedBanks.length]);

  useEffect(() => {
    if (filters) {
      refetch({ throwOnError: false });
    }
  }, [filters, refetch]);

  useEffect(() => {
    if (isRefetchingFromBankChange && !isFetching) {
      setIsRefetchingFromBankChange(false);
    }
  }, [isFetching, isRefetchingFromBankChange]);

  const handlePeriodChange = (id, field, value) => {
    setPeriods((prev) =>
      prev.map((period) => (period.id === id ? { ...period, [field]: value } : period))
    );
  };

  const handleAddPeriod = () => {
    if (periods.length >= MAX_PERIODS) return;

    const lastPeriod = periods[periods.length - 1];
    const baseDate = lastPeriod?.to || lastPeriod?.from || projectionBounds.to || defaultTo;
    const nextFrom = addDays(baseDate, 1);
    const nextTo = addDays(nextFrom, 6);

    setPeriods((prev) => [...prev, { id: createPeriodId(), from: nextFrom, to: nextTo }]);
  };

  const handleRemovePeriod = (id) => {
    if (periods.length === 1) return;
    setPeriods((prev) => prev.filter((period) => period.id !== id));
  };

  const resetFilters = () => {
    setPeriods(buildInitialPeriods(defaultFrom, defaultTo));
    setValidationError('');
    setExportError('');
    setFilters(null);
    setSelectedBanks([...availableBanks]);
  };

  const handleBankToggle = (bankName) => {
    const newSelectedBanks = selectedBanks.includes(bankName)
      ? selectedBanks.filter((bank) => bank !== bankName)
      : [...selectedBanks, bankName];
    
    setSelectedBanks(newSelectedBanks);
    
    if (filters && report) {
      setIsRefetchingFromBankChange(true);
      setTimeout(() => {
        handleGenerateReport(newSelectedBanks);
      }, 0);
    }
  };

  const handleSelectAllBanks = () => {
    const newSelectedBanks = [...allBanks];
    setSelectedBanks(newSelectedBanks);
    
    if (filters && report) {
      setIsRefetchingFromBankChange(true);
      setTimeout(() => {
        handleGenerateReport(newSelectedBanks);
      }, 0);
    }
  };

  const handleDeselectAllBanks = () => {
    const newSelectedBanks = [];
    setSelectedBanks(newSelectedBanks);
    
    if (filters && report) {
      setIsRefetchingFromBankChange(true);
      setTimeout(() => {
        handleGenerateReport(newSelectedBanks);
      }, 0);
    }
  };

  const validateForm = () => {
    for (const [index, period] of periods.entries()) {
      if (!period.from || !period.to) {
        return `Complete las fechas para el periodo ${index + 1}.`;
      }
      if (period.from > period.to) {
        return `En el periodo ${index + 1} la fecha inicial es mayor que la final.`;
      }
    }

    if (!projectionBounds.from || !projectionBounds.to) {
      return 'Complete las fechas en todos los periodos para calcular la proyección.';
    }

    return '';
  };

  const handleGenerateReport = (customSelectedBanks = null) => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }

    setValidationError('');
    setExportError('');

    const banksToUse = customSelectedBanks !== null 
      ? (Array.isArray(customSelectedBanks) ? customSelectedBanks : [])
      : (Array.isArray(selectedBanks) ? selectedBanks : []);
    
    const finalBanksToUse = banksToUse.length === 0 && Array.isArray(availableBanks) 
      ? [...availableBanks] 
      : banksToUse;
    
    const excludedBanks = Array.isArray(availableBanks) && availableBanks.length > 0
      ? availableBanks.filter((bankName) => !finalBanksToUse.includes(bankName))
      : [];

    const payload = {
      projected_from: projectionBounds.from,
      projected_to: projectionBounds.to,
      periods: periods.map((period) => ({
        start_date: period.from,
        end_date: period.to,
      })),
      excluded_banks: excludedBanks || [],
    };

    setFilters(payload);
  };

  const handleExportPdf = async () => {
    if (!report || !report.periods?.length) {
      setExportError('Generá el informe antes de exportar el PDF.');
      return;
    }

    setExportError('');
    setIsExporting(true);

    try {
      await generateProjectedReportPdf({
        report,
        bankColumns,
        formatBankDisplay: formatBankName,
      });
    } catch (err) {
      console.error('Error generando el PDF del informe proyectado:', err);
      setExportError('No se pudo generar el PDF. Intenta nuevamente.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!report || !report.periods?.length) {
      setExportError('Generá el informe antes de exportar a Excel.');
      return;
    }

    setExportError('');
    setIsExportingExcel(true);

    try {
      await exportProjectedReportToExcel(
        report,
        selectedBanks,
        'Informe_Proyectado.xlsx'
      );
    } catch (err) {
      console.error('Error generando el Excel del informe proyectado:', err);
      setExportError('No se pudo generar el archivo Excel. Intenta nuevamente.');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const apiErrorMessage = useMemo(() => {
    if (!isError || !error) {
      return '';
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    return 'Ocurrió un error al generar el informe proyectado.';
  }, [isError, error]);

  const feedbackMessage = validationError || apiErrorMessage || exportError;

  const hasReportData = report?.periods?.length > 0;
  const exportDisabled = !hasReportData || isExporting || isExportingExcel;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <TableTitleBox title="Bancos | Informe proyectado" />

      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <ProjectedReportFilters
          periods={periods}
          onPeriodChange={handlePeriodChange}
          onAddPeriod={handleAddPeriod}
          onRemovePeriod={handleRemovePeriod}
          isMobile={isMobile}
          resetFilters={resetFilters}
          handleGenerateReport={handleGenerateReport}
          handleExportPdf={handleExportPdf}
          handleExportExcel={handleExportExcel}
          exportDisabled={exportDisabled}
          isExporting={isExporting}
          isExportingExcel={isExportingExcel}
          allBanks={allBanks}
          selectedBanks={selectedBanks}
          onBankToggle={handleBankToggle}
          onSelectAllBanks={handleSelectAllBanks}
          onDeselectAllBanks={handleDeselectAllBanks}
        />

        {isRefetchingFromBankChange && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Actualizando reporte con los bancos seleccionados...
          </Alert>
        )}

        {feedbackMessage && <Alert severity="error">{feedbackMessage}</Alert>}
      </Box>

      {isFetching || isRefetchingFromBankChange ? (
        <ProjectedReportTable 
          report={report} 
          isLoading 
          bankColumns={bankColumns}
          isRefetchingFromBankChange={isRefetchingFromBankChange}
        />
      ) : (
        <ProjectedReportTable report={report} bankColumns={bankColumns} />
      )}
    </Box>
  );
};

export default BankProjectedReport;
